
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Role, User, AIEvent, AppConfig, ReviewItem, Theme } from './types';
import { storageService } from './services/storageService';
import { supabaseService } from './services/supabaseService';
import { supabaseAuth, isSupabaseConfigured } from './lib/supabase';
import Sidebar from './components/Sidebar';
import EventTable from './components/EventTable';
import ReviewQueue from './components/ReviewQueue';
import DetailDrawer from './components/DetailDrawer';
import CreateEventModal from './components/CreateEventModal';
import DiscoveryTool from './components/DiscoveryTool';
import AdminSettings from './components/AdminSettings';
import ConfirmDialog from './components/ConfirmDialog';
import Toaster, { ToastType } from './components/Toaster';
import LoginPage from './app/login/page';

const App: React.FC = () => {
  const [view, setView] = useState<'dashboard' | 'discovery' | 'review' | 'admin'>('dashboard');
  const [events, setEvents] = useState<AIEvent[]>([]);
  const [reviewQueue, setReviewQueue] = useState<ReviewItem[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [config, setConfig] = useState<AppConfig>(() => storageService.getConfig());
  const [theme, setTheme] = useState<Theme>(config.theme || 'dark');
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const [confirmDeleteEvent, setConfirmDeleteEvent] = useState<AIEvent | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);

  // We must re-check configuration on mount to catch env vars if storage was stale
  const [isConfigured, setIsConfigured] = useState(isSupabaseConfigured());

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    setToast({ message, type });
  }, []);

  const refreshEvents = useCallback(async (currentConfig: AppConfig) => {
    if (!supabaseService.isConfigured(currentConfig)) return;
    
    setIsLoading(true);
    setFetchError(null);
    try {
      const dbData = await supabaseService.fetchEvents(currentConfig);
      setEvents(dbData);
      storageService.saveEvents(dbData);
      if (dbData.length > 0 && fetchError) setFetchError(null);
    } catch (err: any) {
      const msg = err.message || "Sync Error.";
      setFetchError(msg);
      if (!msg.includes("401") && !msg.includes("RLS")) {
        showToast(msg, "error");
      }
      const cached = storageService.getEvents();
      if (cached.length > 0) setEvents(cached);
    } finally {
      setIsLoading(false);
    }
  }, [showToast, fetchError]);

  useEffect(() => {
    // Force refresh config state to ensure env vars are picked up
    const currentConfig = storageService.getConfig();
    setConfig(currentConfig);
    setIsConfigured(isSupabaseConfigured() || supabaseService.isConfigured(currentConfig));
  }, []);

  useEffect(() => {
    if (!isConfigured) {
      setIsInitializing(false);
      setView('admin');
      return;
    }

    const initAuth = async () => {
      try {
        const { data: { session } } = await supabaseAuth.getSession();
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'Member',
            role: (session.user.user_metadata?.role as Role) || Role.MEMBER,
            avatar: session.user.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${session.user.email}&background=8b3eff&color=000`
          });
        }
      } catch (err) {
        console.error("Auth init failed", err);
      } finally {
        setIsInitializing(false);
      }
    };

    initAuth();

    const { data: { subscription } } = supabaseAuth.onAuthStateChange((_event: string, session: any) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.full_name || session.user.email?.split('@')[0] || 'Member',
          role: (session.user.user_metadata?.role as Role) || Role.MEMBER,
          avatar: session.user.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${session.user.email}&background=8b3eff&color=000`
        });
      } else {
        setUser(null);
        setEvents([]);
      }
    });

    setReviewQueue(storageService.getReviewQueue());
    return () => subscription.unsubscribe();
  }, [isConfigured]);

  useEffect(() => {
    if (user && isConfigured) {
      refreshEvents(config);
    }
  }, [user, isConfigured, config, refreshEvents]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    const newConfig = { ...config, theme };
    storageService.saveConfig(newConfig);
  }, [theme, config]);

  const handleUpdateEvent = async (updatedEvent: AIEvent) => {
    try {
      if (supabaseService.isConfigured(config)) {
        await supabaseService.updateEvent(config, updatedEvent);
      }
      storageService.updateEvent(updatedEvent);
      setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
      showToast("Event updated successfully", "success");
    } catch (err: any) {
      showToast(`Update Error: ${err.message}`, "error");
      throw err;
    }
  };

  const handleManualCreate = async (eventData: Omit<AIEvent, 'id' | 'addedAt'>) => {
    const generateId = (name: string, year: number) => {
      const cleanName = name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 5).toUpperCase();
      const randomSuffix = Math.random().toString(36).substr(2, 4).toUpperCase();
      return `${cleanName}-${year}-${randomSuffix}`;
    };

    const newEvent: AIEvent = {
      ...eventData,
      id: generateId(eventData.campaignName, eventData.year),
      addedAt: new Date().toISOString()
    };

    try {
      if (supabaseService.isConfigured(config)) {
        await supabaseService.insertEvent(config, newEvent);
      }
      storageService.addEvent(newEvent);
      setEvents(prev => [newEvent, ...prev]);
      showToast("Record created successfully", "success");
    } catch (err: any) {
      showToast(`Creation Error: ${err.message}`, "error");
      throw err;
    }
  };

  const executeDelete = async () => {
    if (!confirmDeleteEvent) return;
    const eventId = confirmDeleteEvent.id;
    const campaignName = confirmDeleteEvent.campaignName;
    
    setDeletingId(eventId);
    setConfirmDeleteEvent(null);
    
    const previousEvents = [...events];
    setEvents(prev => prev.filter(e => e.id !== eventId));
    
    try {
      if (supabaseService.isConfigured(config)) {
        await supabaseService.deleteEvent(config, eventId);
        showToast(`Event "${campaignName}" deleted.`, "success");
      } else {
        storageService.deleteEvent(eventId);
        showToast(`Deleted from local cache.`, "success");
      }
      if (selectedEventId === eventId) setSelectedEventId(null);
    } catch (err: any) {
      setEvents(previousEvents);
      showToast(`Delete failed: ${err.message}`, "error");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveConfig = (newCfg: AppConfig) => {
    const cfg = { ...newCfg, theme };
    storageService.saveConfig(cfg);
    setConfig(cfg);
    const newlyConfigured = isSupabaseConfigured() || supabaseService.isConfigured(cfg);
    setIsConfigured(newlyConfigured);
    
    if (newlyConfigured) {
      showToast("Configuration saved. Authenticating...", "success");
      if (user) refreshEvents(cfg);
      setView('dashboard');
    } else {
      showToast("Configuration saved locally", "success");
    }
  };

  const handleLogout = async () => {
    await supabaseAuth.signOut();
    setUser(null);
    setEvents([]);
    setView('dashboard');
    showToast("Signed out successfully", "info");
  };

  const selectedEvent = useMemo(() => events.find(e => e.id === selectedEventId), [events, selectedEventId]);

  const isLight = theme === 'light';

  const connectionStatus = useMemo(() => {
    if (isConfigured) return { label: 'Integrated', color: '#8b3eff' };
    return { label: 'Pending Setup', color: '#9AA0A6' };
  }, [isConfigured]);

  if (isInitializing) {
    return (
      <div className="h-screen w-full bg-charcoal flex flex-col items-center justify-center gap-8">
        <div className="animate-pulse">
          {!logoError ? (
            <img 
              src="https://files.catbox.moe/w8074t.png" 
              alt="EventPulse" 
              className="h-24 w-auto object-contain"
              onError={() => setLogoError(true)}
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center text-charcoal shadow-[0_0_30px_rgba(139,62,255,0.2)]">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <div className="text-center">
                 <div className="text-3xl font-black italic tracking-tighter uppercase text-white leading-none">EventPulse</div>
              </div>
            </div>
          )}
        </div>
        <div className="w-16 h-1 bg-white/5 relative overflow-hidden rounded-full">
          <div className="absolute inset-y-0 left-0 bg-accent w-1/3 animate-[shimmer_1.5s_infinite]"></div>
        </div>
      </div>
    );
  }

  if (!isConfigured) {
    return (
      <div className="min-h-screen bg-charcoal flex flex-col overflow-x-hidden font-sans text-white selection:bg-accent selection:text-white">
        {/* Navigation Bar */}
        <header className="w-full max-w-7xl mx-auto px-8 py-10 flex items-center justify-between border-b border-white/5">
          {!logoError ? (
            <img 
              src="https://files.catbox.moe/w8074t.png" 
              alt="EventPulse" 
              className="h-12 w-auto object-contain" 
              onError={() => setLogoError(true)}
              referrerPolicy="no-referrer"
            />
          ) : (
             <div className="flex items-center gap-3">
               <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center text-charcoal shadow-[0_0_15px_rgba(139,62,255,0.3)]">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
               </div>
               <div className="flex flex-col justify-center">
                 <div className="text-2xl font-black tracking-tighter italic uppercase leading-none text-white">EventPulse</div>
               </div>
            </div>
          )}
          
          <nav className="hidden lg:flex items-center gap-10 text-xs font-bold uppercase tracking-widest text-muted">
            <a href="#" className="hover:text-white transition-colors">Platform</a>
            <a href="#" className="hover:text-white transition-colors">Intelligence</a>
            <a href="#" className="hover:text-white transition-colors">Security</a>
            <a href="#" className="hover:text-white transition-colors">Documentation</a>
          </nav>

          <button className="btn-primary text-xs">
            Connect Stack
          </button>
        </header>

        {/* Hero Section */}
        <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-24 space-y-12 max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-6 duration-1000">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-accent">
            Intelligence Automation
          </div>

          <h1 className="text-6xl md:text-8xl font-black tracking-tight leading-[0.9] text-white">
            AI Powered <span className="highlight-box">Intelligence</span> for the modern enterprise.
          </h1>

          <p className="text-xl text-muted font-medium leading-relaxed max-w-2xl mx-auto">
            Centralize your event intelligence, automate discovery, and scale your reach—with a high-ticket infrastructure you control.
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 pt-4">
            <button className="btn-primary px-12 py-5 text-sm">
              Initialize Platform
            </button>
            <button className="btn-secondary px-12 py-5 text-sm">
              View Architecture
            </button>
          </div>

          {/* Configuration Form below Hero */}
          <div className="w-full pt-32">
            <div className="p-8 bg-surface border border-white/5 rounded-sm shadow-2xl text-left">
                <AdminSettings config={config} onSave={handleSaveConfig} theme="dark" />
            </div>
          </div>
        </div>

        {/* Footer Area */}
        <footer className="w-full max-w-7xl mx-auto px-8 py-20 border-t border-white/5 mt-20 text-center">
           <span className="text-[10px] font-bold text-muted uppercase tracking-widest">© 2025 EVENTPULSE INTEL SYSTEMS • BUILT FOR SCALE</span>
        </footer>
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLoginSuccess={() => refreshEvents(config)} />;
  }

  return (
    <div className={`flex h-screen w-full overflow-hidden transition-colors duration-400 ${theme === 'dark' ? 'bg-charcoal text-white' : 'bg-white text-charcoal'}`}>
      <Sidebar 
        activeView={view} 
        setView={setView} 
        user={user} 
        reviewCount={reviewQueue.filter(r => r.status === 'PENDING').length} 
        theme={theme} 
        setTheme={setTheme} 
        onLogout={handleLogout}
      />
      
      <main className={`flex-1 flex flex-col relative overflow-hidden transition-colors duration-400 ${isLight ? 'bg-[#F8FAFC]' : 'bg-black/20'} border-l ${isLight ? 'border-slate-200' : 'border-white/5'}`}>
        {toast && <Toaster message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        <header className={`h-16 border-b flex items-center justify-between px-10 transition-all duration-400 ${isLight ? 'bg-white/80 border-slate-100 backdrop-blur-md sticky top-0' : 'bg-surface/50 border-white/5 backdrop-blur-md'}`}>
          <div className="flex flex-col">
            <h1 className="text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-3">
              <span className={isLight ? 'text-slate-900' : 'text-white'}>
                {view === 'dashboard' ? 'Current Events' : 
                 view === 'discovery' ? 'Find Events' : 
                 view === 'review' ? 'Review Events' : 
                 view === 'admin' ? 'Core Configuration' : view}
              </span>
              <span className="w-1 h-1 bg-accent rounded-full shadow-[0_0_8px_rgba(139,62,255,0.5)]"></span>
              <span className="text-muted font-normal">Pulse Hub</span>
            </h1>
          </div>
          <div className="flex items-center gap-6">
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full border transition-all ${isLight ? 'bg-slate-50 border-slate-100' : 'bg-white/5 border-white/10'}`}>
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted">{connectionStatus.label}</span>
            </div>
            <button 
              onClick={() => refreshEvents(config)} 
              disabled={isLoading}
              className="p-2 text-muted hover:text-accent transition-all disabled:opacity-30"
              title="Sync Protocol"
            >
              <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357-2H15" />
              </svg>
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-8 xl:p-10">
          <div className="max-w-7xl mx-auto">
            {view === 'dashboard' && (
              <EventTable 
                events={events} 
                onSelectEvent={setSelectedEventId} 
                onDeleteEvent={setConfirmDeleteEvent} 
                onManualCreate={() => setIsCreateModalOpen(true)}
                deletingId={deletingId} 
                isLoading={isLoading} 
                fetchError={fetchError} 
                theme={theme} 
              />
            )}
            {view === 'discovery' && <DiscoveryTool config={config} existingEvents={events} onDiscoverComplete={() => { setReviewQueue(storageService.getReviewQueue()); setView('review'); }} theme={theme} />}
            {view === 'review' && <ReviewQueue queue={reviewQueue} config={config} onUpdate={() => setReviewQueue(storageService.getReviewQueue())} onEventsApproved={() => refreshEvents(config)} theme={theme} />}
            {view === 'admin' && <AdminSettings config={config} onSave={handleSaveConfig} theme={theme} />}
          </div>
        </div>
      </main>

      {selectedEvent && (
        <DetailDrawer 
          event={selectedEvent} 
          onClose={() => setSelectedEventId(null)} 
          currentUser={user} 
          theme={theme} 
          onUpdateEvent={handleUpdateEvent} 
          config={config} 
        />
      )}
      
      <CreateEventModal 
        isOpen={isCreateModalOpen} 
        onClose={() => setIsCreateModalOpen(false)} 
        onSave={handleManualCreate} 
        theme={theme} 
      />

      <ConfirmDialog
        isOpen={!!confirmDeleteEvent}
        title="Destroy Record"
        message={<>Permanent removal of <span className={`font-bold underline decoration-accent ${isLight ? 'text-slate-900' : 'text-white'}`}>{confirmDeleteEvent?.campaignName}</span> from intelligence database?</>}
        onConfirm={executeDelete}
        onCancel={() => setConfirmDeleteEvent(null)}
        isProcessing={!!deletingId}
      />
    </div>
  );
};

export default App;
